import React, { useState, useEffect } from 'react';
import { useContent } from '../../hooks/useContent';

function generateId() {
  return 'x' + Math.random().toString(36).substr(2, 8);
}

const EMPTY_CAFE = { heading: '', subheading: '', intro: '', intro2: '', seasonNote: '', hours: [], menu: [] };

export default function ContentEditor() {
  const { content, loading, error, saveContent } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (content && !draft) {
      const d = JSON.parse(JSON.stringify(content));
      if (!d.cafe) d.cafe = { ...EMPTY_CAFE };
      setDraft(d);
    }
  }, [content, draft]);

  if (loading) return <p style={styles.info}>Lade Inhalte...</p>;
  if (error) return <p style={styles.errorText}>Fehler: {error}</p>;
  if (!draft) return null;

  // Generic nested-path setter (e.g. 'hero.heading')
  const set = (path, value) => {
    const keys = path.split('.');
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
    setSaved(false);
  };

  // ── Highlights ──────────────────────────────────────────────
  const updateHighlight = (index, field, value) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.highlights[index][field] = value;
      return next;
    });
    setSaved(false);
  };
  const addHighlight = () => {
    setDraft(prev => ({
      ...prev,
      highlights: [...(prev.highlights || []), { id: generateId(), icon: '⭐', title: 'Neu', text: '' }],
    }));
    setSaved(false);
  };
  const removeHighlight = (index) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.highlights.splice(index, 1);
      return next;
    });
    setSaved(false);
  };

  // ── Café: Öffnungszeiten ─────────────────────────────────────
  const updateHour = (index, field, value) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.hours[index][field] = value;
      return next;
    });
    setSaved(false);
  };
  const addHour = () => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.hours.push({ id: generateId(), day: 'Montag – Sonntag', time: '08:00 – 18:00 Uhr' });
      return next;
    });
    setSaved(false);
  };
  const removeHour = (index) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.hours.splice(index, 1);
      return next;
    });
    setSaved(false);
  };

  // ── Café: Speisekarte ────────────────────────────────────────
  const updateMenuCategory = (ci, field, value) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.menu[ci][field] = value;
      return next;
    });
    setSaved(false);
  };
  const addMenuCategory = () => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.menu.push({ id: generateId(), category: 'Neue Kategorie', icon: '🍽️', items: [] });
      return next;
    });
    setSaved(false);
  };
  const removeMenuCategory = (ci) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.menu.splice(ci, 1);
      return next;
    });
    setSaved(false);
  };
  const updateMenuItem = (ci, ii, field, value) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.menu[ci].items[ii][field] = value;
      return next;
    });
    setSaved(false);
  };
  const addMenuItem = (ci) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.menu[ci].items.push({ id: generateId(), name: 'Neu', desc: '', price: '0,00 €' });
      return next;
    });
    setSaved(false);
  };
  const removeMenuItem = (ci, ii) => {
    setDraft(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.cafe.menu[ci].items.splice(ii, 1);
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveContent(draft);
      setSaved(true);
    } catch (e) {
      alert('Fehler beim Speichern: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.editor}>

      {/* ── Hero ── */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Hero-Bereich</h3>
        <Field label="Hauptüberschrift" value={draft.hero?.heading || ''} onChange={v => set('hero.heading', v)} />
        <Field label="Untertitel" value={draft.hero?.subheading || ''} onChange={v => set('hero.subheading', v)} />
      </section>

      {/* ── Über uns ── */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Über uns</h3>
        <Field label="Überschrift" value={draft.ueberUns?.heading || ''} onChange={v => set('ueberUns.heading', v)} />
        <Field label="Text" value={draft.ueberUns?.text || ''} onChange={v => set('ueberUns.text', v)} multiline />
      </section>

      {/* ── Highlights ── */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Highlights</h3>
        {(draft.highlights || []).map((h, i) => (
          <div key={h.id} style={styles.highlightRow}>
            <div style={styles.hlFields}>
              <Field label={`Icon ${i + 1}`} value={h.icon} onChange={v => updateHighlight(i, 'icon', v)} small />
              <Field label="Titel" value={h.title} onChange={v => updateHighlight(i, 'title', v)} />
              <Field label="Text" value={h.text} onChange={v => updateHighlight(i, 'text', v)} />
            </div>
            <button style={styles.removeBtn} onClick={() => removeHighlight(i)} title="Entfernen">✕</button>
          </div>
        ))}
        <button style={styles.addBtn} onClick={addHighlight}>+ Highlight hinzufügen</button>
      </section>

      {/* ── Kontakt ── */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Kontakt</h3>
        <Field label="Name" value={draft.contact?.name || ''} onChange={v => set('contact.name', v)} />
        <Field label="Adresse" value={draft.contact?.address || ''} onChange={v => set('contact.address', v)} />
        <Field label="Telefon" value={draft.contact?.phone || ''} onChange={v => set('contact.phone', v)} />
        <Field label="E-Mail" value={draft.contact?.email || ''} onChange={v => set('contact.email', v)} />
      </section>

      {/* ── Café ── */}
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>Café</h3>

        <Field label="Seitenüberschrift" value={draft.cafe?.heading || ''} onChange={v => set('cafe.heading', v)} />
        <Field label="Untertitel" value={draft.cafe?.subheading || ''} onChange={v => set('cafe.subheading', v)} />
        <Field label="Einleitungstext 1" value={draft.cafe?.intro || ''} onChange={v => set('cafe.intro', v)} multiline />
        <Field label="Einleitungstext 2" value={draft.cafe?.intro2 || ''} onChange={v => set('cafe.intro2', v)} multiline />

        {/* Öffnungszeiten */}
        <h4 style={styles.subSectionTitle}>Öffnungszeiten</h4>
        {(draft.cafe?.hours || []).map((h, i) => (
          <div key={h.id} style={styles.hoursRow}>
            <div style={styles.hoursFields}>
              <Field label="Tag / Zeitraum" value={h.day} onChange={v => updateHour(i, 'day', v)} />
              <Field label="Uhrzeit" value={h.time} onChange={v => updateHour(i, 'time', v)} />
            </div>
            <button style={styles.removeBtn} onClick={() => removeHour(i)} title="Entfernen">✕</button>
          </div>
        ))}
        <button style={styles.addBtn} onClick={addHour}>+ Öffnungszeit hinzufügen</button>
        <Field label="Saisonhinweis (unter den Öffnungszeiten)" value={draft.cafe?.seasonNote || ''} onChange={v => set('cafe.seasonNote', v)} />

        {/* Speisekarte */}
        <h4 style={styles.subSectionTitle}>Speisekarte</h4>
        {(draft.cafe?.menu || []).map((cat, ci) => (
          <div key={cat.id} style={styles.menuCatBlock}>
            <div style={styles.menuCatHeader}>
              <Field label="Icon" value={cat.icon} onChange={v => updateMenuCategory(ci, 'icon', v)} small />
              <div style={{ flex: 1 }}>
                <Field label="Kategoriename" value={cat.category} onChange={v => updateMenuCategory(ci, 'category', v)} />
              </div>
              <button style={styles.removeCatBtn} onClick={() => removeMenuCategory(ci)}>✕ Kategorie</button>
            </div>
            {(cat.items || []).map((item, ii) => (
              <div key={item.id} style={styles.menuItemRow}>
                <div style={{ flex: 2 }}><Field label="Name" value={item.name} onChange={v => updateMenuItem(ci, ii, 'name', v)} /></div>
                <div style={{ flex: 3 }}><Field label="Beschreibung" value={item.desc} onChange={v => updateMenuItem(ci, ii, 'desc', v)} /></div>
                <div style={{ flex: 1 }}><Field label="Preis" value={item.price} onChange={v => updateMenuItem(ci, ii, 'price', v)} /></div>
                <button style={styles.removeBtn} onClick={() => removeMenuItem(ci, ii)} title="Entfernen">✕</button>
              </div>
            ))}
            <button style={styles.addBtnSmall} onClick={() => addMenuItem(ci)}>+ Gericht hinzufügen</button>
          </div>
        ))}
        <button style={styles.addBtn} onClick={addMenuCategory}>+ Kategorie hinzufügen</button>
      </section>

      <div style={styles.saveRow}>
        {saved && <span style={styles.savedNote}>✓ Gespeichert</span>}
        <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'Speichern...' : 'Änderungen speichern'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, small }) {
  return (
    <div style={fieldStyles.group}>
      <label style={fieldStyles.label}>{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={4}
          style={fieldStyles.textarea}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          style={{ ...fieldStyles.input, ...(small ? { maxWidth: '80px' } : {}) }}
        />
      )}
    </div>
  );
}

const styles = {
  editor: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  section: { backgroundColor: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.5rem' },
  sectionTitle: { color: '#333', fontFamily: 'Arial, sans-serif', fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #28a745' },
  subSectionTitle: { color: '#555', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', fontWeight: '700', marginTop: '1.25rem', marginBottom: '0.75rem', paddingBottom: '0.35rem', borderBottom: '1px solid #e5e5e5' },

  highlightRow: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '6px' },
  hlFields: { flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' },

  hoursRow: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.6rem', padding: '0.75rem', backgroundColor: '#f9f9f9', borderRadius: '6px' },
  hoursFields: { flex: 1, display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },

  menuCatBlock: { border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', backgroundColor: '#f9f9f9' },
  menuCatHeader: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e0e0e0' },
  menuItemRow: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #e5e7eb', flexWrap: 'wrap' },

  removeBtn: { background: 'none', border: 'none', color: '#dc3545', fontSize: '1.2rem', cursor: 'pointer', padding: '0.3rem', lineHeight: 1, marginTop: '0.3rem', flexShrink: 0 },
  removeCatBtn: { background: 'none', border: '1px solid #dc3545', color: '#dc3545', fontSize: '0.8rem', borderRadius: '4px', cursor: 'pointer', padding: '0.3rem 0.6rem', alignSelf: 'flex-end', marginBottom: '0.75rem', whiteSpace: 'nowrap' },
  addBtn: { backgroundColor: '#f0f9f1', color: '#28a745', border: '1px dashed #28a745', borderRadius: '5px', padding: '0.6rem 1.2rem', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem', width: '100%', marginTop: '0.25rem' },
  addBtnSmall: { backgroundColor: '#f0f9f1', color: '#28a745', border: '1px dashed #28a745', borderRadius: '5px', padding: '0.4rem 0.9rem', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '0.82rem', marginTop: '0.5rem' },

  saveRow: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem' },
  savedNote: { color: '#28a745', fontFamily: 'Arial, sans-serif', fontSize: '0.9rem' },
  saveBtn: { backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', padding: '0.8rem 2rem', fontSize: '1rem', fontWeight: '700', cursor: 'pointer', fontFamily: 'Arial, sans-serif' },
  info: { color: '#666', fontFamily: 'Arial, sans-serif' },
  errorText: { color: '#dc3545', fontFamily: 'Arial, sans-serif' },
};

const fieldStyles = {
  group: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.75rem' },
  label: { fontSize: '0.85rem', fontWeight: '600', color: '#555', fontFamily: 'Arial, sans-serif' },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.95rem', fontFamily: 'Arial, sans-serif' },
  textarea: { padding: '0.55rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.95rem', fontFamily: 'Arial, sans-serif', resize: 'vertical' },
};
